import { useEffect, useRef } from 'react';
import { Label } from '../ui/label';
import { Lock, Building2, CheckCircle2, Check } from 'lucide-react';
import { FieldInput, ReadOnlyValue } from './FieldInput';
import { formatValue, isEmptyValue } from './bookingUtils';

export const ClientHeader = ({ branding, logoSrc, templateName, bookingId, customerName }) => (
  <header className="bg-[#0F172A] text-white border-b-2 border-[#D4AF37] shadow-lg py-6 px-4 sm:px-8" data-testid="client-form-header">
    <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        {logoSrc ? (
          <img src={logoSrc} alt={branding?.company_name || 'Logo'} className="h-12 sm:h-14 object-contain" data-testid="client-form-logo" />
        ) : (
          <div className="h-12 w-12 rounded-lg bg-[#D4AF37] text-[#0F172A] font-heading font-bold text-xl flex items-center justify-center shrink-0">
            {(branding?.company_name || 'G').charAt(0)}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-heading text-xl sm:text-2xl leading-tight truncate" data-testid="client-form-company">{branding?.company_name || 'Goldsands Group Inc'}</p>
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/60">Official Booking Form</p>
        </div>
      </div>
      <div className="sm:text-right text-sm space-y-0.5 shrink-0">
        <p className="font-medium text-white/90" data-testid="client-form-template-name">{templateName}</p>
        {customerName && <p className="text-white/60 text-xs">Prepared for <span className="text-white/90">{customerName}</span></p>}
        <p className="font-mono text-[11px] text-[#D4AF37]/90">Ref {bookingId}</p>
      </div>
    </div>
  </header>
);

export const ClientStepper = ({ steps, current, onJump }) => {
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' }); }, [current]);
  return (
  <nav className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex items-center gap-1 overflow-x-auto" data-testid="client-stepper" aria-label="Form progress">
    {steps.map((step, idx) => {
      const state = idx < current ? 'done' : idx === current ? 'active' : 'upcoming';
      return (
        <button key={step.key} type="button" ref={state === 'active' ? activeRef : null} onClick={() => idx < current && onJump(idx)} disabled={idx > current}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm whitespace-nowrap transition-colors ${
            state === 'active' ? 'bg-[#0F172A] text-white font-semibold' : state === 'done' ? 'text-emerald-700 hover:bg-emerald-50 font-medium' : 'text-slate-400'
          }`} data-testid={`client-step-${idx}`}>
          <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center shrink-0 ${
            state === 'active' ? 'bg-[#D4AF37] text-[#0F172A] font-bold' : state === 'done' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'
          }`}>
            {state === 'done' ? <Check className="w-3 h-3" strokeWidth={3} /> : idx + 1}
          </span>
          {step.name}
        </button>
      );
    })}
  </nav>
  );
};

export const ClientField = ({ field, value, officeValue, onChange, disabled }) => {
  const locked = field.is_office_only;
  return (
    <div className={`rounded-xl border p-4 sm:p-5 transition-colors ${
      locked ? 'bg-slate-50/80 border-slate-200' : 'bg-white border-[#D4AF37]/40 focus-within:border-[#D4AF37] shadow-sm'
    }`} data-testid={`client-field-row-${field.field_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <Label htmlFor={field.field_id} className="text-sm sm:text-[15px] font-semibold text-slate-900 leading-snug">
          {field.label}{!locked && field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {locked ? (
          <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-slate-200/70 text-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="prefilled-badge">
            <Lock className="w-3 h-3" /> Pre-filled by office
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 shrink-0 rounded-full bg-[#D4AF37]/10 text-[#856404] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="your-response-badge">
            Your response
          </span>
        )}
      </div>
      {locked ? (
        <ReadOnlyValue value={officeValue} placeholder="—" testId={`client-locked-${field.field_id}`} />
      ) : (
        <FieldInput field={field} value={value} onChange={onChange} disabled={disabled} testId={`client-input-${field.field_id}`} />
      )}
      {field.help_text && <p className="text-xs text-slate-500 mt-2">{field.help_text}</p>}
    </div>
  );
};

export const ClientSection = ({ section, index, formData, officeResponses, onChange, disabled }) => {
  const officeOnly = (section.fields || []).length > 0 && section.fields.every(f => f.is_office_only);
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-[#0F172A] text-[#D4AF37] font-bold flex items-center justify-center shrink-0">{index + 1}</span>
        <div>
          <h2 className="font-heading text-2xl text-slate-900 leading-tight">{section.title}</h2>
          {section.description && <p className="text-slate-500 text-sm mt-1">{section.description}</p>}
        </div>
      </div>
      {officeOnly && (
        <div className="flex items-start gap-2 rounded-lg bg-slate-100 border border-slate-200 px-4 py-3 text-sm text-slate-700" data-testid="office-only-notice">
          <Building2 className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          This section was completed by our office for your reference. Please review the details — no input is required from you.
        </div>
      )}
      <div className="space-y-4">
        {(section.fields || []).map(field => (
          <ClientField key={field.field_id} field={field} value={formData[field.field_id]} officeValue={officeResponses[field.field_id]}
            onChange={(v) => onChange(field.field_id, v)} disabled={disabled} />
        ))}
      </div>
    </div>
  );
};

export const ReviewSummary = ({ sections, formData, officeResponses, uploads, uploadedFiles, declaration, acknowledged, signature, onJump }) => (
  <div className="space-y-6" data-testid="review-summary">
    <div>
      <h2 className="font-heading text-2xl text-slate-900">Review &amp; Submit</h2>
      <p className="text-slate-500 text-sm mt-1">Please check everything below before submitting. You can go back to any section to make changes.</p>
    </div>
    {sections.map((section, idx) => (
      <div key={section.section_id} className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-800">{idx + 1}. {section.title}</p>
          <button type="button" onClick={() => onJump(idx)} className="text-xs font-medium text-[#856404] hover:underline" data-testid={`review-edit-${idx}`}>Edit</button>
        </div>
        <dl className="divide-y divide-slate-100">
          {(section.fields || []).map(field => {
            const val = field.is_office_only ? officeResponses[field.field_id] : formData[field.field_id];
            const empty = isEmptyValue(val);
            return (
              <div key={field.field_id} className="grid grid-cols-1 sm:grid-cols-5 gap-1 sm:gap-4 px-4 py-2.5 text-sm">
                <dt className="sm:col-span-2 text-slate-500 flex items-center gap-1.5">
                  {field.is_office_only && <Lock className="w-3 h-3 text-slate-400" />}{field.label}
                </dt>
                <dd className={`sm:col-span-3 ${empty ? 'text-slate-400 italic' : 'text-slate-900 font-medium'} whitespace-pre-wrap`}>
                  {empty ? (field.required && !field.is_office_only ? 'Required — not answered' : 'Not provided') : formatValue(val)}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    ))}
    {uploads?.length > 0 && (
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-sm font-semibold text-slate-800">Documents</div>
        <ul className="divide-y divide-slate-100">
          {uploads.map(u => (
            <li key={u.upload_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-slate-600">{u.label}</span>
              {uploadedFiles[u.upload_id] ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{uploadedFiles[u.upload_id].name}</span> : <span className="text-slate-400 italic">Not uploaded</span>}
            </li>
          ))}
        </ul>
      </div>
    )}
    {declaration && (
      <div className="flex flex-wrap gap-4 text-sm">
        {declaration.require_ack_checkbox && <span className={acknowledged ? 'text-emerald-700' : 'text-red-600'}>{acknowledged ? '✓ Declaration acknowledged' : '✗ Declaration not acknowledged'}</span>}
        {declaration.require_signature && <span className={signature ? 'text-emerald-700' : 'text-red-600'}>{signature ? '✓ Signed' : '✗ Signature missing'}</span>}
      </div>
    )}
  </div>
);
